import * as THREE from "three";
import { immerable } from "immer";
import fileContentParser from "@/pages/takeoff/layouts/organisation/helpers/file-content-parser.ts";
import Segment from "@/models/interfaces/segment.ts";
import ScaleModeType from "@/pages/takeoff/context/project-organisation/models/enums/file-content-type";
import PlanOrigin from "@/pages/takeoff/context/project-organisation/models/plan-origin";
import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import PlanFormat from "../../project-organisation/types/plan-format";
import PlanOrientation from "../../project-organisation/types/plan-orientation";
import PlanScale from "../../project-organisation/types/plan-scale";

class PlanFileData {
    [immerable] = true;

    isDirty = false;

    origin: PlanOrigin;

    content: KnownScaleContent | UserDefinedContent | undefined;

    origins: PlanOrigin[];

    sitePosition: THREE.Vector3 | undefined;

    setContentType(contentType: ScaleModeType) {
        switch (contentType) {
            case ScaleModeType.KNOWN:
                this.content = new KnownScaleContent();
                this.content.default = true;
                break;
            case ScaleModeType.USER:
            default:
                this.content = new UserDefinedContent();
                break;
        }
    }

    public setDirtyFlag(isDirty: boolean) {
        if (isDirty && this.content instanceof KnownScaleContent)
            this.content.default = false;
        this.isDirty = isDirty;
    }

    public setOrigin(originId: string) {
        const found = this.origins.find(o => o.id === originId);
        if (!found) return;

        this.origin = found;

        this.origins.forEach(o => {
            o.setIsActive(o.id === found.id);
        });

        this.origin.isActive = true;
    }

    public resetOrigin() {
        this.origins.forEach(o => {
            o.setIsActive(false);
        });
    }

    public setRelativeOrigin(originId: string | undefined) {
        const active = this.origins.find(o => o.isActive);
        if (!active) return;

        active.relativeOrigineId = originId;

        this.origin.relativeOrigineId = originId;
    }

    public addOrigin(origin: PlanOrigin) {
        this.origins.push(origin);
    }

    public deleteOrigin(originId: string) {
        const foundIndex = this.origins.findIndex(o => o.id === originId);
        if (foundIndex === -1) return;

        this.origins.splice(foundIndex, 1);
    }

    /* Valeurs représentés par le plan PDF */

    public setFormat(format: PlanFormat) {
        if (this.content instanceof KnownScaleContent)
            this.content.format = format;
    }

    public setOrientation(orientation: PlanOrientation) {
        if (this.content instanceof KnownScaleContent)
            this.content.orientation = orientation;
    }

    public setScale(scale: PlanScale) {
        if (this.content instanceof KnownScaleContent)
            this.content.scale = scale;
    }

    /* Valeurs données par l'utilisateur */

    public setUserMeasuredSegment(segment: Segment) {
        if (this.content instanceof UserDefinedContent)
            this.content.segment = segment;
    }

    public setUserMeasuredScale(scale: number) {
        if (this.content instanceof UserDefinedContent)
            this.content.scale = scale;
    }

    constructor() {
        this.origin = PlanOrigin.getDefault();
        this.origins = [];
        
        this.content = new KnownScaleContent();
        this.content.default = true;
    }

    public static fromJSON(data: Record<string, unknown>): PlanFileData {
        const plan = new PlanFileData();

        const mode = (data.mode || data.Mode) as ScaleModeType;
        const origin = data.origin || data.Origin;
        if (origin && typeof origin === 'object')
            plan.origin = PlanOrigin.fromJSON(origin as Record<string, unknown>);

        if (data.content || data.Content)
            plan.content = fileContentParser(mode, (data.content || data.Content) as Record<string, unknown>);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sitePosition = (data.sitePosition || data.SitePosition) as any;
        if (sitePosition && typeof sitePosition === 'object')
            plan.sitePosition = new THREE.Vector3(
                sitePosition.x || sitePosition.X,
                sitePosition.y || sitePosition.Y, 
                sitePosition.z || sitePosition.Z
            );

        return plan;
    }

    clone() {
        const plan = new PlanFileData();
        plan.origin = this.origin.clone();
        plan.origins = this.origins.map(o => o.clone());
        plan.content = this.content?.clone();
        return plan;
    }

    hydrate(origin: PlanOrigin, origins: PlanOrigin[], content: KnownScaleContent | UserDefinedContent | undefined) {
        this.origin = origin;
        this.origins = origins;
        this.content = content;
    }
}

export default PlanFileData;